/* eslint-disable no-unused-vars */

import moment from 'moment';
import Delta from 'quill-delta';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { GetDownloadLink } from 'store/actions/generalActions';
import { smartcomplyLogoBase64 } from '../../../db';
import { extractDocName, resizeURLImageToBase64 } from '../../../utils';
import {
    insertImageIntoDelta,
    insertNewLineIntoDelta,
    insertSubTitleIntoDelta,
    insertTableIntoDelta,
    insertTitleIntoDelta,
} from '../../PolicyEditorDrawer/Editor/utils/db';
import ReadOnlyEditor from '../../PolicyEditorDrawer/ReadOnlyEditor';
import NotSupported from './NotSupported';

export default function MainContent({ currentVersion, allDocuments }) {
    const [organizationLogo, setOrganizationLogo] = useState('');

    const raw_all_employees = useSelector((state) => state?.generalReducers?.all_employees);
    const user_info = useSelector((state) => state?.authReducers?.user_info);

    const userDetails = useMemo(
        () => ({
            id: user_info?.organization?.owner_detail?.id,
            user_detail: {
                id: user_info?.organization?.owner_detail?.id,
                first_name: user_info?.organization?.owner_detail?.first_name,
                last_name: user_info?.organization?.owner_detail?.last_name,
                email: user_info?.organization?.owner_detail?.email,
                user_type: user_info?.organization?.owner_detail?.user_type,
            },
        }),
        [user_info]
    );

    const all_employees = useMemo(
        () =>
            !raw_all_employees
                ? []
                : [...raw_all_employees, userDetails]?.map((employee) => ({
                      ...employee,
                      name: employee?.user_detail?.first_name
                          ? `${employee?.user_detail?.first_name} ${employee?.user_detail?.last_name}`
                          : employee?.user_detail?.email,
                  })),
        [raw_all_employees, userDetails]
    );

    const departmentsData = useMemo(
        () =>
            currentVersion?.notify_all_employees
                ? []
                : currentVersion?.acceptance_history?.accepted
                      ?.concat(currentVersion?.acceptance_history?.pending)
                      ?.map((data) => ({
                          ...data,
                          name:
                              data?.first_name || data?.last_name
                                  ? data?.first_name + ' ' + data?.last_name
                                  : data?.email,
                      })),
        [currentVersion]
    );

    const isDocEditorDocument = useMemo(() => {
        const docExtension = (currentVersion?.filename || currentVersion?.document)?.split('.').pop();
        return ['pdf', 'docx'].includes(docExtension) && currentVersion?.is_editor;
    }, [currentVersion]);

    const revisionDelta = useMemo(() => {
        if (!currentVersion || !isDocEditorDocument || !currentVersion?.filename) return null;

        const currentVersionIndex = allDocuments?.findIndex((doc) => doc?.id === currentVersion?.id);

        const editorCurrentWidth =
            document.querySelector('.read-only .ql-container.ql-snow .ql-editor')?.offsetWidth - 194;

        const delta = new Delta().insert('\n');

        const step1 = insertImageIntoDelta(organizationLogo || smartcomplyLogoBase64, delta);
        const step2 = insertNewLineIntoDelta(8, step1);
        const step3 = insertTitleIntoDelta(
            extractDocName(currentVersion?.filename || currentVersion?.document)
                ?.split('.')
                ?.shift()
                ?.replaceAll('_', ' '),
            step2
        );
        const step4 = insertNewLineIntoDelta(4, step3);
        const step5 = insertTableIntoDelta(
            [editorCurrentWidth / 4, editorCurrentWidth / 4, editorCurrentWidth / 4, editorCurrentWidth / 4],
            [
                ['Version', 'Date', 'Change Number', 'Summary of Changes'],
                [
                    currentVersion?.version_no || currentVersion?.version,
                    moment(currentVersion?.date_updated).format('DD/MM/YYYY'),
                    currentVersion?.change_no?.toString(),
                    currentVersion?.change_summary,
                ],
                [
                    allDocuments?.[currentVersionIndex + 1]?.version_no || '',
                    allDocuments?.[currentVersionIndex + 1]
                        ? moment(allDocuments?.[currentVersionIndex + 1]?.date_updated).format('DD/MM/YYYY')
                        : '',
                    allDocuments?.[currentVersionIndex + 1]?.change_no?.toString() || '',
                    allDocuments?.[currentVersionIndex + 1]?.change_summary || '',
                ],
                [
                    allDocuments?.[currentVersionIndex + 2]?.version_no || '',
                    allDocuments?.[currentVersionIndex + 2]
                        ? moment(allDocuments?.[currentVersionIndex + 2]?.date_updated).format('DD/MM/YYYY')
                        : '',
                    allDocuments?.[currentVersionIndex + 2]?.change_no?.toString() || '',
                    allDocuments?.[currentVersionIndex + 2]?.change_summary || '',
                ],
                [
                    allDocuments?.[currentVersionIndex + 3]?.version_no || '',
                    allDocuments?.[currentVersionIndex + 3]
                        ? moment(allDocuments?.[currentVersionIndex + 3]?.date_updated).format('DD/MM/YYYY')
                        : '',
                    allDocuments?.[currentVersionIndex + 3]?.change_no?.toString() || '',
                    allDocuments?.[currentVersionIndex + 3]?.change_summary || '',
                ],
            ],
            step4
        );
        const step6 = insertNewLineIntoDelta(1, step5);
        const step7 = insertSubTitleIntoDelta('Document Review', step6);
        const step8 = insertNewLineIntoDelta(1, step7);
        const step9 = insertTableIntoDelta(
            [editorCurrentWidth],
            [
                ['Date of Next Scheduled Review'],
                [
                    moment(currentVersion?.date_updated)
                        .add(
                            currentVersion?.reminder_date?.[0]?.interval,
                            currentVersion?.reminder_date?.[0]?.frequency
                        )
                        .format('DD/MM/YYYY'),
                ],
            ],
            step8
        );
        const step10 = insertNewLineIntoDelta(1, step9);

        let conditionalStep;

        if (departmentsData?.length > 0) {
            const step11 = insertSubTitleIntoDelta('Distribution', step10);
            const step12 = insertNewLineIntoDelta(1, step11);
            const step13 = currentVersion?.notify_all_employees
                ? insertTableIntoDelta(
                      [editorCurrentWidth],
                      [[''], ['This document has been distributed to all employees.']],
                      step12
                  )
                : insertTableIntoDelta(
                      [editorCurrentWidth / 2, editorCurrentWidth / 2],
                      [
                          ['Name', 'Department'],
                          ...departmentsData.map((data) => [data?.name, data?.department]),
                          ['', ''],
                          ['', ''],
                      ],
                      step12
                  );
            conditionalStep = insertNewLineIntoDelta(1, step13);
        } else {
            conditionalStep = step10;
        }

        const step15 = insertSubTitleIntoDelta('Approval', conditionalStep || step10);
        const step16 = insertNewLineIntoDelta(1, step15);
        const step17 = insertTableIntoDelta(
            [editorCurrentWidth / 4, editorCurrentWidth / 4, editorCurrentWidth / 4, editorCurrentWidth / 4],
            [
                ['', 'Name', 'Position', 'Date'],
                [
                    'Developed By#bold',
                    all_employees?.find((emp) => emp?.user_detail?.id === currentVersion?.author)?.name,
                    all_employees?.find((emp) => emp?.user_detail?.id === currentVersion?.author)?.user_detail
                        ?.user_type,
                    moment(currentVersion?.date_updated).format('DD/MM/YYYY'),
                ],
                [
                    'Approved By#bold',
                    all_employees?.find((emp) => emp?.user_detail?.id === currentVersion?.approved_by)?.name || 'N/A',
                    all_employees?.find((emp) => emp?.user_detail?.id === currentVersion?.approved_by)?.user_detail
                        ?.user_type || 'N/A',
                    all_employees?.find((emp) => emp?.user_detail?.id === currentVersion?.approved_by)
                        ? moment(currentVersion?.date_updated).format('DD/MM/YYYY')
                        : 'N/A',
                ],
                ['', '', '', ''],
            ],
            step16
        );
        const step18 = insertNewLineIntoDelta(4, step17);

        return step18;
    }, [all_employees, departmentsData, currentVersion, isDocEditorDocument, organizationLogo, allDocuments]);

    const processOrganizationLogo = useCallback(async () => {
        if (!user_info?.organization?.id) return;

        const user_type =
            user_info.user_type === 'implementer'
                ? 'implementer'
                : user_info.user_type === 'auditor'
                ? 'auditor'
                : 'merchant';

        try {
            const urlResponse = await GetDownloadLink('user_mgt', user_type, user_info?.organization?.id)();
            if (urlResponse?.success) {
                const resizedBase64 = await resizeURLImageToBase64(urlResponse?.data, 60);
                setOrganizationLogo(resizedBase64);
            } else {
                throw new Error('Failed to fetch organization logo');
            }
        } catch (error) {
            console.error('Error fetching organization logo:', error);
            setOrganizationLogo('');
        }
    }, [user_info]);

    useEffect(() => {
        processOrganizationLogo();
    }, [processOrganizationLogo]);

    return (
        <div className="!my-6 !ml-4 !mr-6 !rounded bg-white !h-[calc(100vh-120px)] overflow-y-auto overflow-x-auto custom-scroll !w-full">
            {!isDocEditorDocument ? (
                <div className="w-full h-full grid items-center">
                    <NotSupported currentVersion={currentVersion} />
                </div>
            ) : (
                <ReadOnlyEditor
                    readOnlyDocument={currentVersion}
                    delta={currentVersion?.operations}
                    revisionDelta={revisionDelta}
                />
            )}
        </div>
    );
}
